import React, { useState, useEffect, VFC, useRef } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import { DataLayerContext } from '@/components/common/DataLayerProviderContainer';
import BaseButton from '@/components/common/Button/BaseButton';
import WarningList from '@/components/common/Direct/WarningList';
import { ButtonShadowIconButton } from '@/components/common/Button/ShadowIconButton';

import ImgStep1 from '@/assets/images/OtherComponents/input_agency-step1.webp';
import ImgStep2 from '@/assets/images/OtherComponents/input_agency-step2.webp';
import ImgStep3 from '@/assets/images/OtherComponents/input_agency-step3.webp';

import { toggleLoading } from '@/redux/index';
import { UserApi } from '@/utils/api-client/api';

type TProps = {
  pageNumber?: number;
  inputAgencyStatus: number | undefined;
  isClose: () => void;
  fileData: boolean;
  isOpen: boolean;
  getInputAgency?: () => void;
};

/**
 * https://www.canva.com/design/DAGYZr5H9ms/e0iQ1fuybpC4GdkJ6SjHFA/edit
 */

export const ModalEntry: VFC<TProps> = ({
  pageNumber,
  inputAgencyStatus,
  isClose,
  fileData,
  isOpen,
  getInputAgency,
}) => {
  const dispatch = useDispatch();
  // ====================================================================================================
  //
  // setState
  //
  // ====================================================================================================
  const [centerMode, setCenterMode] = useState<boolean>(true);
  const [number, setNumber] = useState<number>(pageNumber ? pageNumber : 0);
  const [status, setInputAgencyStatus] = useState<number>(
    inputAgencyStatus ? inputAgencyStatus : 0
  );
  const { push } = React.useContext(DataLayerContext);
  const childRef = useRef<HTMLDivElement>(null);

  const changeNumber = (n?: number) => {
    if (n || n === 0) {
      setNumber(n);
    } else {
      setNumber(number + 1);
    }
  };

  const clickRequest = () => {
    if (status !== 1 && fileData) {
      dispatch(toggleLoading(true));
      (async () => {
        const input_agency = await new UserApi().postInputAgency();
        if (input_agency.status === 200) {
          setNumber(100);
          setInputAgencyStatus(1);
          if (getInputAgency) getInputAgency();
          dispatch(toggleLoading(false));
        }
      })();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    push({
      event: 'pageView',
      actionType: 'page_view',
      actionName: 'modal_open',
    });
  }, [isOpen]);

  useEffect(() => {
    if (typeof pageNumber === 'number') setNumber(pageNumber);
  }, [pageNumber]);

  useEffect(() => {
    if (childRef.current) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      }
    }
    if (!isOpen) {
      document.body.style.overflow = 'auto';
    }
    changeMode();
  }, [isOpen]);

  useEffect(() => {
    changeMode();
  }, [number]);

  const changeMode = () => {
    if (childRef.current) {
      const childHeight = childRef.current.clientHeight;
      const windowHeight = window.innerHeight;
      if (childHeight > windowHeight) setCenterMode(false);
      else setCenterMode(true);
    }
  };

  return (
    <div className="modal">
      {isOpen && (
        <div className={cn('modal-overlay', centerMode ? 'modal-overlay_center' : null)}>
          <div className="modal-inner">
            <div className={cn('content')}>
              <div className={cn('child rounded-modalContainer')} ref={childRef}>
                {number !== 100 ? (
                  <div className={`max-w-784 w-full py-48 px-60 sm:py-32 sm:px-32`}>
                    {number === 0 && (
                      <div>
                        <p className="mbx-typography--heading_3 text-center mb-32">
                          まずは履歴書・職務経歴書を登録しましょう。
                        </p>
                        <img src={ImgStep1} width="690" height="363" alt="" className="w-690" />
                        <p className="mbx-typography--body_1 mt-32">
                          会員様が自作の履歴書・職務経歴書を登録（アップロード）すると、
                          <br />
                          入力代行サービスに申し込むことができます。
                        </p>
                      </div>
                    )}
                    {number === 1 && (
                      <div>
                        <p className="mbx-typography--heading_3 text-center mb-32">
                          準備が出来たら申し込みましょう。
                        </p>
                        <img src={ImgStep2} width="690" height="363" alt="" className="w-690" />
                        <p className="mbx-typography--body_1 mt-32">
                          MATCHBOX事務局が申し込み日から5営業日ほどで入力代行を実施し、
                          <br />
                          完了後にメールにてお知らせいたします。
                        </p>
                        <p className="mbx-typography--caption_1 mt-16">
                          ※入力代行が不可と判断した場合はご対応を見送る場合がございます。
                        </p>
                      </div>
                    )}
                    {number === 2 && (
                      <div>
                        <p className="mbx-typography--heading_3 text-center mb-32">
                          入力代行の内容を確認・反映すると完了です。
                        </p>
                        <img src={ImgStep3} width="690" height="363" alt="" className="w-690" />
                        <p className="mbx-typography--body_1 mt-32">
                          会員様がメール記載のURLから入力代行内容を確認し、必要であれば内容を編集したうえで
                          <br />
                          MATCHBOXに反映すると入力代行が完了します。
                        </p>
                      </div>
                    )}
                    {number === 3 && (
                      <div>
                        <p className="mbx-typography--heading_3 text-center mb-32">
                          入力代行サービスに申し込む
                        </p>
                        <p className="mbx-typography--body_1 my-32">
                          会員様がメール記載のURLから入力代行内容を確認し、必要であれば内容を編集したうえで
                          <br />
                          MATCHBOXに反映すると入力代行が完了します。
                        </p>
                        <WarningList
                          warnings={[
                            '入力代行の依頼中は自作の履歴書・職務経歴書の登録（アップロード）の上書き・削除ができなくなりますのでご注意ください。',
                            '入力代行が対応可能な言語は日本語のみとなります。',
                            '入力代行する箇所は履歴書・職務経歴書のみとなりますので、それ以外の基本情報などの項目は編集しません。',
                            '登録（アップロード）されたファイルを、MATCHBOX事務局で確認・審査し通過された会員様のみご対応いたします。「情報量が極端に少ない」「内容が転職活動に無関係である」など、企業からオファーを受け取るために有用な情報量や内容でないと判断した場合や「手書き」「画像形式」などテキスト・写真を選択できない場合は、ご対応を見送る可能性がございます。',
                            'MATCHBOXの履歴書・職務経歴書の編集画面にすでに入力情報がある場合や誤った情報がある場合でも、登録（アップロード）されたファイルの内容を最新かつ正しい情報とみなし原則そのまま転記を行います。',
                            '当サービスは、原則、転記のみの入力代行となりますが、マイナビワークスの転職支援を行ってきた知見をもとに一部内容を編集する場合もあります。また、MATCHBOXの入力フォームの上限文字数に合わせて、文字数を削減しなくてはいけない場合などは、言葉尻の調整を加えるなどの軽微な編集をする場合がございます。',
                            '入力代行完了通知メールを受信後は、必ず会員様ご自身で入力内容を確認し、「反映を確定する」ボタンでMATCHBOXへの反映をお願いします。反映後は入力代行前の内容に戻すことはできませんのでご注意ください。',
                          ]}
                        />
                      </div>
                    )}
                    <ul className="mt-16">
                      {[...Array(4)].map((_, i) => (
                        <li
                          key={i}
                          className={cn(
                            'inline-block bg-gray-100 w-8 h-8 rounded-full mx-4 cursor-pointer',
                            {
                              'bg-red-700': number === i,
                            }
                          )}
                          onClick={() => setNumber(i)}
                        ></li>
                      ))}
                    </ul>
                    <div className="text-center">
                      {(number === 0 || number === 1 || number === 2) && (
                        <div className="mt-32 mx-auto w-240">
                          <BaseButton
                            theme="primary"
                            size="m"
                            className="w-full"
                            width="large"
                            onClick={() => changeNumber()}
                          >
                            {(number === 0 || number === 1) && <>次へ</>}
                            {number === 2 && <>申込み画面へ</>}
                          </BaseButton>
                        </div>
                      )}
                      {(number === 0 || number === 1) && (
                        <div className="mt-16 sm:mt-8">
                          <BaseButton
                            theme="link"
                            size="m"
                            width="large"
                            onClick={() => changeNumber(3)}
                          >
                            説明をスキップする
                          </BaseButton>
                        </div>
                      )}
                      {number === 3 && (
                        <>
                          <div
                            className={cn('mt-32 mx-auto w-300 relative', {
                              'mbx-tooltip--hover': !fileData,
                            })}
                          >
                            <BaseButton
                              theme="primary"
                              size="m"
                              className="w-full"
                              width="large"
                              disabled={status === 1 || !fileData}
                              onClick={() => clickRequest()}
                            >
                              {status === 1 ? <>入力代行を依頼中</> : <>申し込む</>}
                            </BaseButton>
                            {!fileData && status !== 1 && (
                              <div className="mbx-admin_tooltip" style={{ top: 30, left: 0 }}>
                                履歴書もしくは職務経歴書をアップロードすることで入力代行サービスに申し込みできるようになります。
                              </div>
                            )}
                          </div>
                          <div className="mt-16 mx-auto w-300 sm:mt-8">
                            <BaseButton
                              theme="secondary"
                              size="m"
                              className="w-full"
                              width="large"
                              onClick={() => changeNumber(0)}
                            >
                              サービスの流れをもう一度確認する
                            </BaseButton>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="lg:w-624 sm:pb-80 lg:p-80 md:p-80 sm:py-44 sm:px-24 bg-cover bg-no-repeat bg-center"
                    style={{ backgroundImage: `url(/images/lively_bg.png)` }}
                  >
                    <p className="mbx-typography--heading_3 text-center mb-32">
                      入力代行サービスの申し込みが完了しました。
                    </p>
                    <p className="mbx-typography--body_1 mt-32">
                      入力代行完了後、メールにてお知らせいたします。
                      5営業日ほどお時間をいただいておりますのでお待ちください。
                    </p>
                    <p className="mbx-typography--caption_1 mt-16">
                      ※入力代行が不可と判断した場合はご対応を見送る場合がございます。
                    </p>
                    <div className="mt-32 mx-auto w-300">
                      <BaseButton
                        theme="secondary"
                        size="m"
                        className="w-full"
                        width="large"
                        onClick={() => changeNumber(0)}
                      >
                        サービスの流れをもう一度確認する
                      </BaseButton>
                    </div>
                    <div className="mt-16">
                      <BaseButton theme="link" size="m" width="large" onClick={() => isClose()}>
                        閉じる
                      </BaseButton>
                    </div>
                  </div>
                )}
              </div>
              <div className={cn('btn-area')}>
                <ButtonShadowIconButton
                  className={cn('close-icon', centerMode ? 'close-icon_center' : null)}
                  iconName={'Circle-large_Clear'}
                  onClick={isClose}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
